import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { getForecastRunStatus } from "@/api/services/forecastService";
import { useApp } from "@/contexts/AppContext";
import { useUsers } from "@/contexts/UsersContext";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ForecastRefreshStatus = "idle" | "in-progress" | "complete";

export interface ForecastRefreshState {
  status: ForecastRefreshStatus;
  startTime: Date | null;
  completionTime: Date | null;
  triggeredBy: string; // Display name of the user who triggered it
  estimatedDurationMs: number; // Estimated duration in milliseconds
  runId: number | null; // Id of the run being tracked (null until known)
}

interface ForecastRefreshContextType {
  refreshState: ForecastRefreshState;
  startRefresh: (triggeredBy: string, estimatedDurationMs: number) => void;
  setTriggeredRunId: (runId: number | null) => void;
  completeRefresh: () => void;
  resetRefresh: () => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const ForecastRefreshContext = createContext<
  ForecastRefreshContextType | undefined
>(undefined);

const INITIAL_STATE: ForecastRefreshState = {
  status: "idle",
  startTime: null,
  completionTime: null,
  triggeredBy: "",
  estimatedDurationMs: 0,
  runId: null,
};

const ACTIVE_STATUSES = ["pending", "in-progress"];
const TERMINAL_STATUSES = ["completed", "failed", "timed-out"];

const POLLING_INTERVAL_MS = 3_000;

export const ForecastRefreshProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { mode } = useApp();
  const { getUserById } = useUsers();
  const queryClient = useQueryClient();
  const [refreshState, setRefreshState] =
    useState<ForecastRefreshState>(INITIAL_STATE);
  // Timer ref so we can cancel the auto-dismiss on reset
  const autoHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshStateRef = useRef<ForecastRefreshState>(INITIAL_STATE);
  const getUserByIdRef = useRef(getUserById);

  // Keep refs in sync so polling callback always has latest values
  useEffect(() => {
    refreshStateRef.current = refreshState;
  }, [refreshState]);
  useEffect(() => {
    getUserByIdRef.current = getUserById;
  }, [getUserById]);

  // Invalidate every query that feeds the forecast charts so they refetch.
  // Covers the REST timeline/metrics (["forecast", ...]), the ground-truth
  // GraphQL base forecast view (["baseForecastGraphql"]) that the 90-day /
  // 12-month / 3-year charts read from, and scenario overlays (["scenarios"]).
  const invalidateForecastQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["forecast"] });
    queryClient.invalidateQueries({ queryKey: ["baseForecastGraphql"] });
    queryClient.invalidateQueries({ queryKey: ["scenarios"] });
    // A successful forecast run cascades scenario refreshes (re-pin +
    // re-materialise). Refresh the scenario views so the freshness badges and
    // the scenario refresh banner pick up the newly-triggered "Updating" state.
    queryClient.invalidateQueries({ queryKey: ["scenarioViews"] });
    queryClient.invalidateQueries({ queryKey: ["updatingScenarios"] });
  }, [queryClient]);

  // A completed run's status flips before the base-forecast read model is
  // guaranteed to reflect the new run, so an immediate refetch can land on
  // stale rows (the "F5 a moment later works" symptom). Invalidate again
  // shortly after completion to pick up the fresh data automatically.
  const invalidateForecastQueriesWithRetry = useCallback(() => {
    invalidateForecastQueries();
    if (refetchTimer.current) {
      clearTimeout(refetchTimer.current);
    }
    refetchTimer.current = setTimeout(() => {
      invalidateForecastQueries();
      refetchTimer.current = null;
    }, 4_000);
  }, [invalidateForecastQueries]);

  // Clear the delayed refetch timer on unmount
  useEffect(() => {
    return () => {
      if (refetchTimer.current) {
        clearTimeout(refetchTimer.current);
        refetchTimer.current = null;
      }
    };
  }, []);

  const startRefresh = useCallback(
    (triggeredBy: string, estimatedDurationMs: number) => {
      if (autoHideTimer.current) {
        clearTimeout(autoHideTimer.current);
        autoHideTimer.current = null;
      }
      setRefreshState({
        status: "in-progress",
        startTime: new Date(),
        completionTime: null,
        triggeredBy,
        estimatedDurationMs,
        runId: null,
      });
      invalidateForecastQueries();
    },
    [invalidateForecastQueries],
  );

  // Attach the id of the run we just triggered so the poller only treats
  // that specific run's completion as "complete" (avoids mistaking a
  // previously-completed run for the new one).
  const setTriggeredRunId = useCallback((runId: number | null) => {
    setRefreshState((prev) =>
      prev.status === "in-progress" ? { ...prev, runId } : prev,
    );
  }, []);

  const completeRefresh = useCallback(() => {
    setRefreshState((prev) => ({
      ...prev,
      status: "complete",
      completionTime: new Date(),
    }));
    invalidateForecastQueriesWithRetry();
    // Auto-fade to idle after 30 seconds
    autoHideTimer.current = setTimeout(() => {
      setRefreshState((prev) => {
        if (prev.status === "complete") {
          return INITIAL_STATE;
        }
        return prev;
      });
      autoHideTimer.current = null;
    }, 30_000);
  }, [invalidateForecastQueriesWithRetry]);

  const resetRefresh = useCallback(() => {
    if (autoHideTimer.current) {
      clearTimeout(autoHideTimer.current);
      autoHideTimer.current = null;
    }
    setRefreshState(INITIAL_STATE);
  }, []);

  // ─── Live mode polling ───────────────────────────────────────────────────────
  useEffect(() => {
    if (mode !== "live") return;

    const poll = async () => {
      try {
        const statusResponse = await getForecastRunStatus();
        const currentState = refreshStateRef.current;

        if (
          ACTIVE_STATUSES.includes(statusResponse.status) &&
          currentState.status === "idle"
        ) {
          const rawTriggeredBy = statusResponse.triggeredBy ?? "System";
          const triggeredBy =
            rawTriggeredBy === "System"
              ? "System"
              : (getUserByIdRef.current(rawTriggeredBy)?.name ??
                rawTriggeredBy);
          const estimatedMs = statusResponse.estimatedDurationMs ?? 0;
          setRefreshState({
            status: "in-progress",
            startTime: statusResponse.startedAt
              ? new Date(statusResponse.startedAt)
              : new Date(),
            completionTime: null,
            triggeredBy,
            estimatedDurationMs: estimatedMs,
            runId: statusResponse.forecastRunId,
          });
        } else if (
          currentState.status === "in-progress" &&
          currentState.runId !== null &&
          statusResponse.forecastRunId === currentState.runId &&
          TERMINAL_STATUSES.includes(statusResponse.status)
        ) {
          setRefreshState((prev) => ({
            ...prev,
            status: "complete",
            completionTime: statusResponse.completedAt
              ? new Date(statusResponse.completedAt)
              : new Date(),
          }));
          invalidateForecastQueriesWithRetry();
          autoHideTimer.current = setTimeout(() => {
            setRefreshState((prev) => {
              if (prev.status === "complete") {
                return INITIAL_STATE;
              }
              return prev;
            });
            autoHideTimer.current = null;
          }, 30_000);
        }
      } catch {
        // Silently ignore polling errors — will retry on next interval
      }
    };

    // Run immediately on mount to detect in-progress runs
    poll();
    pollingRef.current = setInterval(poll, POLLING_INTERVAL_MS);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [mode]);

  return (
    <ForecastRefreshContext.Provider
      value={{
        refreshState,
        startRefresh,
        setTriggeredRunId,
        completeRefresh,
        resetRefresh,
      }}
    >
      {children}
    </ForecastRefreshContext.Provider>
  );
};

export const useForecastRefresh = (): ForecastRefreshContextType => {
  const ctx = useContext(ForecastRefreshContext);
  if (!ctx) {
    throw new Error(
      "useForecastRefresh must be used within ForecastRefreshProvider",
    );
  }
  return ctx;
};
